/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getPresetById } from '../theme/presets';
import { derivePaletteFromBase, hexToRgbChannels, SHADE_STOPS } from '../lib/brandPalette';

export interface LogoSettings {
  logoPreview: string;
  darkLogoPreview: string;
  mobileLogoPreview: string;
  faviconPreview: string;
  logoAltText: string;
  logoWidth: number;
  logoHeight: number;
  logoPadding: { top: number; right: number; bottom: number; left: number };
  logoLink: string;
  logoNewTab: boolean;
}

/** Workspace persistence status exposed on the context (optional, additive) */
export interface WorkspacePersistenceStatus {
  /** Whether workspace persistence is currently active (enabled + DB row present) */
  isActive: boolean;
  /** Source of the currently applied brand values */
  source: 'local' | 'workspace';
}

/** Metadata about the current workspace brand state, exposed for governance UI */
export interface WorkspaceBrandMeta {
  updatedAt: string | null;
  updatedByName: string | null;
}

interface BrandThemeContextType {
  primaryColor: string;
  accentColor: string;
  signatureColor: string;
  saturation: number;
  lightness: number;
  themePreset: string;
  isWhiteLabelEnabled: boolean;
  logoSettings: LogoSettings;
  setPrimaryColor: (color: string) => void;
  setAccentColor: (color: string) => void;
  setSignatureColor: (color: string) => void;
  adjustSaturation: (value: number) => void;
  adjustLightness: (value: number) => void;
  applyThemePreset: (preset: string) => void;
  setWhiteLabelEnabled: (enabled: boolean) => void;
  /** Apply workspace-level brand data (called by ThemeSettings when DB row loads) */
  applyWorkspaceBrand?: (brand: {
    primaryColor: string;
    accentColor: string;
    signatureColor: string;
    presetId: string | null;
    logoSettings: Partial<LogoSettings> | null;
  }) => void;
  /** Workspace persistence status (undefined = not wired yet) */
  workspacePersistence?: WorkspacePersistenceStatus;
  /** Notify the provider that workspace persistence state changed */
  setWorkspacePersistence?: (status: WorkspacePersistenceStatus) => void;
  /** True when workspace-level branding is active (is_enabled = true + row exists) */
  isWorkspaceBrandingActive?: boolean;
  /** Metadata about the active workspace brand (updated_at, updated_by_name) */
  workspaceBrandMeta?: WorkspaceBrandMeta;
  /** Set workspace brand meta from the hook layer */
  setWorkspaceBrandMeta?: (meta: WorkspaceBrandMeta) => void;
}

const defaultLogoSettings: LogoSettings = {
  logoPreview: '',
  darkLogoPreview: '',
  mobileLogoPreview: '',
  faviconPreview: '',
  logoAltText: 'AX1 | Capital',
  logoWidth: 180,
  logoHeight: 40,
  logoPadding: { top: 8, right: 0, bottom: 8, left: 0 },
  logoLink: '/',
  logoNewTab: false
};

const BrandThemeContext = createContext<BrandThemeContextType | undefined>(undefined);

// Helper to load logo settings from localStorage
const loadLogoSettings = (): LogoSettings => {
  try {
    const saved = localStorage.getItem('brandTheme_logoSettings');
    if (saved) {
      return { ...defaultLogoSettings, ...JSON.parse(saved) };
    }
  } catch {
    // Ignore parse errors
  }
  return defaultLogoSettings;
};

export function BrandThemeProvider({ children }: { children: React.ReactNode }) {
  const [primaryColor, setPrimaryColor] = useState(() =>
    localStorage.getItem('brandTheme_primaryColor') || '#2B63C5'
  );
  const [accentColor, setAccentColor] = useState(() =>
    localStorage.getItem('brandTheme_accentColor') || '#3F4FA3'
  );
  const [signatureColor, setSignatureColor] = useState(() =>
    localStorage.getItem('brandTheme_signatureColor') || '#475569'
  );
  const [saturation, setSaturation] = useState(0);
  const [lightness, setLightness] = useState(0);
  const [themePreset, setThemePreset] = useState(() =>
    localStorage.getItem('brandTheme_preset') || 'ax1_core'
  );
  const [isWhiteLabelEnabled, setIsWhiteLabelEnabled] = useState(() =>
    localStorage.getItem('brandTheme_whiteLabelEnabled') === 'true'
  );

  // Logo settings - loaded from localStorage
  const [logoSettings, setLogoSettingsState] = useState<LogoSettings>(loadLogoSettings);

  // Re-load logo settings when localStorage changes (from ThemeSettings save)
  useEffect(() => {
    const handleStorageChange = () => {
      setLogoSettingsState(loadLogoSettings());
    };

    // Listen for storage events (from other tabs) and custom event (same tab)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('brandTheme_logoSettingsUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('brandTheme_logoSettingsUpdated', handleStorageChange);
    };
  }, []);

  // Update favicon when logo settings change
  useEffect(() => {
    if (isWhiteLabelEnabled && logoSettings.faviconPreview) {
      // Find existing favicon or create new one
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = logoSettings.faviconPreview;

      // Also update apple-touch-icon if present
      const touchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
      if (touchIcon) {
        touchIcon.href = logoSettings.faviconPreview;
      }
    }
  }, [isWhiteLabelEnabled, logoSettings.faviconPreview]);

  // Toggle white label function
  const setWhiteLabelEnabled = (enabled: boolean) => {
    setIsWhiteLabelEnabled(enabled);
    localStorage.setItem('brandTheme_whiteLabelEnabled', enabled.toString());
  };

  // Apply theme variables to :root (only if white label is enabled)
  // Tracks dark-mode state so we can derive a dark-optimized palette.
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  // Observe .dark class mutations on <html> — fires synchronously on toggle
  useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains('dark'));
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Ref to avoid double-inject races when dark mode + color both change in one frame
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Use rAF to coalesce rapid dark-toggle + colour changes into a single write
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      applyBrandTokens();
    });
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };

    function applyBrandTokens() {
      const root = document.documentElement.style;

      // — Brand token custom properties managed by BrandThemeProvider —
      // Build list dynamically so it includes the palette stops.
      const BRAND_PROPS: string[] = [
        '--brand-primary',
        '--brand-primary-hover',
        '--brand-primary-muted',
        '--brand-primary-subtle',
        '--brand-accent',
        '--brand-accent-hover',
        '--brand-accent-muted',
        '--brand-signature',
        '--brand-signature-rgb',
        // Legacy compat (existing UI may still reference these)
        '--color-primary-500',
        '--color-accent-500',
        '--color-signature',
        '--color-signature-shadow',
        '--color-signature-glow',
      ];
      // Stage 2: palette stops (50..900) for primary & accent, hex + rgb
      for (const stop of SHADE_STOPS) {
        BRAND_PROPS.push(
          `--brand-primary-${stop}`,
          `--brand-primary-${stop}-rgb`,
          `--brand-accent-${stop}`,
          `--brand-accent-${stop}-rgb`,
        );
      }

      if (!isWhiteLabelEnabled) {
        // Remove all brand overrides → semantic layer falls back to AX1 defaults
        BRAND_PROPS.forEach((p) => root.removeProperty(p));
        return;
      }

      // ── Compute brand variants ────────────────────────────────────
      const darkerHex = darkenColor(primaryColor, 12);
      const lighterHex = lightenColor(primaryColor, 85);
      const subtleHex = lightenColor(primaryColor, 92);

      const accentDarker = darkenColor(accentColor, 12);
      const accentLighter = lightenColor(accentColor, 85);

      // Layer 3: Brand tokens (consumed by semantic.css via var() fallback)
      root.setProperty('--brand-primary', primaryColor);
      root.setProperty('--brand-primary-hover', darkerHex);
      root.setProperty('--brand-primary-muted', lighterHex);
      root.setProperty('--brand-primary-subtle', subtleHex);
      root.setProperty('--brand-accent', accentColor);
      root.setProperty('--brand-accent-hover', accentDarker);
      root.setProperty('--brand-accent-muted', accentLighter);
      root.setProperty('--brand-signature', signatureColor);
      root.setProperty('--brand-signature-rgb', hexToRgbChannels(signatureColor));

      // Legacy compat — keep existing CSS variable names working
      root.setProperty('--color-primary-500', primaryColor);
      root.setProperty('--color-accent-500', accentColor);
      root.setProperty('--color-signature', signatureColor);
      root.setProperty('--color-signature-shadow', `${signatureColor}40`);
      root.setProperty('--color-signature-glow', `${signatureColor}80`);

      // ── Stage 2/10: inject brand-derived palette (50..900, hex + rgb) ──
      // Use dark-optimized curve when .dark is active — compresses 50–300
      // to low lightness so bg-primary-50/10 remains a subtle dark overlay.
      const paletteMode = isDark ? 'dark' : 'light';
      const primaryPalette = derivePaletteFromBase(primaryColor, { mode: paletteMode });
      const accentPalette = derivePaletteFromBase(accentColor, { mode: paletteMode });

      for (const stop of SHADE_STOPS) {
        root.setProperty(`--brand-primary-${stop}`, primaryPalette[stop].hex);
        root.setProperty(`--brand-primary-${stop}-rgb`, primaryPalette[stop].rgb);
        root.setProperty(`--brand-accent-${stop}`, accentPalette[stop].hex);
        root.setProperty(`--brand-accent-${stop}-rgb`, accentPalette[stop].rgb);
      }

      // Persist to localStorage
      localStorage.setItem('brandTheme_primaryColor', primaryColor);
      localStorage.setItem('brandTheme_accentColor', accentColor);
      localStorage.setItem('brandTheme_signatureColor', signatureColor);
      localStorage.setItem('brandTheme_preset', themePreset);
     
     
    }
   
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryColor, accentColor, signatureColor, themePreset, isWhiteLabelEnabled, isDark]);

  // Apply adjustments
  const adjustSaturation = (value: number) => {
    setSaturation(value);
    // In a real implementation, this would use HSL color manipulation to adjust
    // all color tokens based on the saturation value
  };

  const adjustLightness = (value: number) => {
    setLightness(value);
    // In a real implementation, this would use HSL color manipulation to adjust
    // all color tokens based on the lightness value
  };

  // Simple helper to darken a hex color
  const darkenColor = (color: string, percent: number) => {
    const rgb = parseHex(color);
    if (!rgb) return color;
    const [r, g, b] = rgb;
    const dr = Math.max(0, Math.floor(r * (100 - percent) / 100));
    const dg = Math.max(0, Math.floor(g * (100 - percent) / 100));
    const db = Math.max(0, Math.floor(b * (100 - percent) / 100));
    return toHex(dr, dg, db);
  };

  // Helper to lighten a hex color (mix toward white)
  const lightenColor = (color: string, percent: number) => {
    const rgb = parseHex(color);
    if (!rgb) return color;
    const [r, g, b] = rgb;
    const mix = percent / 100;
    const lr = Math.min(255, Math.round(r + (255 - r) * mix));
    const lg = Math.min(255, Math.round(g + (255 - g) * mix));
    const lb = Math.min(255, Math.round(b + (255 - b) * mix));
    return toHex(lr, lg, lb);
  };

  // Parse hex → [r,g,b]
  const parseHex = (color: string): [number, number, number] | null => {
    const m = color.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return null;
    return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
  };

  const toHex = (r: number, g: number, b: number) =>
    `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

  // Apply theme presets (uses centralized presets from src/theme/presets.ts)
  const applyThemePreset = (preset: string) => {
    setThemePreset(preset);

    const found = getPresetById(preset);
    if (found) {
      setPrimaryColor(found.primary);
      setAccentColor(found.accent);
      setSignatureColor(found.signature);
    }

    setSaturation(0);
    setLightness(0);
  };

  // ── Workspace persistence layer (additive, optional) ──
  const [workspacePersistence, setWorkspacePersistence] = useState<WorkspacePersistenceStatus>({
    isActive: false,
    source: 'local',
  });

  const [workspaceBrandMeta, setWorkspaceBrandMeta] = useState<WorkspaceBrandMeta>({
    updatedAt: null,
    updatedByName: null,
  });

  const isWorkspaceBrandingActive = workspacePersistence.isActive;

  /**
   * Apply workspace-level brand data to the local state.
   * Called by ThemeSettings / useWorkspaceBrandTheme when DB row is loaded.
   * This pushes DB values into the same state atoms that local mode uses,
   * so all downstream CSS variable injection works unchanged.
   */
  const applyWorkspaceBrand = React.useCallback((brand: {
    primaryColor: string;
    accentColor: string;
    signatureColor: string;
    presetId: string | null;
    logoSettings: Partial<LogoSettings> | null;
  }) => {
    setPrimaryColor(brand.primaryColor);
    setAccentColor(brand.accentColor);
    setSignatureColor(brand.signatureColor);
    if (brand.presetId) setThemePreset(brand.presetId);
    if (brand.logoSettings) {
      const merged = { ...defaultLogoSettings, ...brand.logoSettings };
      setLogoSettingsState(merged);
      // Also persist to localStorage so other tabs pick it up
      localStorage.setItem('brandTheme_logoSettings', JSON.stringify(merged));
      window.dispatchEvent(new Event('brandTheme_logoSettingsUpdated'));
    }
    // Ensure white-label is on if workspace branding is being applied
    if (!isWhiteLabelEnabled) {
      setIsWhiteLabelEnabled(true);
      localStorage.setItem('brandTheme_whiteLabelEnabled', 'true');
    }
  }, [isWhiteLabelEnabled]);

  return (
    <BrandThemeContext.Provider value={{
      primaryColor,
      accentColor,
      signatureColor,
      saturation,
      lightness,
      themePreset,
      isWhiteLabelEnabled,
      logoSettings,
      setPrimaryColor,
      setAccentColor,
      setSignatureColor,
      adjustSaturation,
      adjustLightness,
      applyThemePreset,
      setWhiteLabelEnabled,
      applyWorkspaceBrand,
      workspacePersistence,
      setWorkspacePersistence,
      isWorkspaceBrandingActive,
      workspaceBrandMeta,
      setWorkspaceBrandMeta,
    }}>
      {children}
    </BrandThemeContext.Provider>
  );
}

export function useBrandTheme() {
  const context = useContext(BrandThemeContext);
  if (context === undefined) {
    throw new Error('useBrandTheme must be used within a BrandThemeProvider');
  }
  return context;
}